import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import reduce from 'lodash/reduce';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import has from 'lodash/has';
import mapKeys from 'lodash/mapKeys';
import isString from 'lodash/isString';
import isObjectLike from 'lodash/isObjectLike';
import camelCase from 'lodash/camelCase';

import { validate } from 'src/client/js/facades/eagle/validation';

import Inscription from './Inscription';
import Adventurer from './Adventurer';
import Formats from './Formats';
import HomeAddress from './HomeAddress';
import Interest from './Interest';
import Birthday from './Birthday';
import Companion from './Companion';
import BearFam from './BearFam';
import FamilyMembers from './FamilyMembers';
import Qualities from './Qualities';
import MultiAdventurer from './MultiAdventurer';
import MultiBirthday from './MultiBirthday';

import {
  trackAdventurerDrawerSaveTab,
  trackAdventurerChange,
  trackGenderChange,
  trackNameChange,
  trackMultiAdventurerDrawerOpenTab,
  trackMultiAdventurerDrawerCloseTab,
  trackMultiAdventurerDrawerCutomize,
  trackMultiAdventurerAddNew,
  trackMultiAdventurerDrawerSaveTab,
  trackMultiAdventurerDrawerSaveAdventurer,
  trackMultiAdventurerNameChange,
  trackMiultiAdventurerGenderChange,
  trackMultiAdventurerPhototypeChange,
  trackMultiBirthdaySelectedDay,
  trackMultiBirthdaySelectedMonth,
  trackMultiBirthdaySelectedYear,
} from '../../../analytics/category/product';

const fieldsWithPossibleWhiteSpace = ['name', 'surname', 'inscription'];
const config = {
  inscription: {
    get component() {
      return Inscription;
    },
    fields: {
      required: ['inscription'],
    },
  },
  adventurer: {
    get component() {
      return Adventurer;
    },
    get trackingProps() {
      return {
        trackSaveAdventurer: trackAdventurerDrawerSaveTab,
        trackNameChange: trackNameChange,
        trackGenderChange: trackGenderChange,
        trackPhototypeChange: trackAdventurerChange,
      };
    },
    fields: {
      required: ['name', 'gender'], // By default the name and gender are set to required for all books
      overrides: {                  // Below we override the exceptions for other books
        LMN: [
          {
            locale: '*',
            required: ['name', 'gender', 'locale'],
          },
          {
            locale: 'en-GB',
            required: ['name', 'gender', 'locale', 'phototype'],
          },
          {
            locale: 'en-US',
            required: ['name', 'gender', 'locale', 'phototype'],
          },
        ],
        MGT: [
          {
            locale: '*',
            required: ['name', 'gender', 'surname'],
          },
        ],
        MLM: [
          {
            locale: '*',
            required: ['name'],
          },
        ],
        KOY: [
          {
            locale: '*',
            required: ['name', 'gender', 'phototype'],
          },
        ],
        TPW: [
          {
            locale: '*',
            required: ['name', 'gender', 'phototype'],
          },
        ],
        TBT: [
          {
            locale: '*',
            required: ['name', 'gender', 'phototype'],
          },
        ],
        SCS: [
          {
            locale: '*',
            required: ['locale'],
          },
        ],
        WKN: [
          {
            locale: '*',
            required: ['name'],
          },
        ],
        HBY: [
          {
            locale: '*',
            required: ['name', 'gender'],
          },
        ],
      },
    },
  },
  book_formats: {
    get component() {
      return Formats;
    },
    fields: {
      required: ['format_identifier'],
    },
  },
  address: {
    get component() {
      return HomeAddress;
    },
    fields: {
      required: ['door', 'street', 'city', 'country', 'lat', 'lng'],
    },
  },
  interest: {
    get component() {
      return Interest;
    },
    fields: {
      required: [],
      overrides: {
        KOY: [
          {
            locale: '*',
            type: 'interest',
            required: ['interest'],
          },
          {
            locale: '*',
            type: 'food',
            required: ['food'],
          },
        ],
        TPW: [
          {
            locale: '*',
            type: 'superhero',
            required: ['superhero'],
          },
        ],
        WKN: [
          {
            locale: '*',
            required: ['cover_style'], // manually set the required interest
          },
        ],
        HBY: [
          {
            locale: '*',
            required: ['cover_style'],
          },
        ],
        MLM: [
          {
            locale: '*',
            required: ['cover_style']
          },
        ]
      },
    },
  },
  companion: {
    get component() {
      return Companion;
    },
    isOptional: true,
    fields: {
      required: ['gifter_name', 'gifter_gender'],
      overrides: {
        YAE: [
          {
            locale: '*',
            required: ['gifter_name'],
          },
        ]
      }
    },
  },
  birthday: {
    get component() {
      return Birthday;
    },
    fields: {
      required: ['day', 'month', 'year'],
    },
  },
  bears: {
    get component() {
      return BearFam;
    },
    fields: {
      required: ['bears'],
    },
  },
  family_members: {
    get component() {
      return FamilyMembers;
    },
    fields: {
      required: ['names'],
    },
  },
  qualities: {
    get component() {
      return Qualities;
    },
    fields: {
      required: ['values'],
      overrides: {
        CWY: [
          {
            locale: '*',
            required: ['wishes'],
          },
        ]
      }
    }
  },
  multi_adventurer: {
    get component() {
      return MultiAdventurer;
    },
    get trackingProps() {
      return {
        trackOpenTab: trackMultiAdventurerDrawerOpenTab,
        trackCloseTab: trackMultiAdventurerDrawerCloseTab,
        trackSaveTab: trackMultiAdventurerDrawerSaveTab,
        trackCustomiseTab: trackMultiAdventurerDrawerCutomize,
        trackAddNewAdventurer: trackMultiAdventurerAddNew,
        trackSaveAdventurer: trackMultiAdventurerDrawerSaveAdventurer,
        trackNameChange: trackMultiAdventurerNameChange,
        trackGenderChange: trackMiultiAdventurerGenderChange,
        trackPhototypeChange: trackMultiAdventurerPhototypeChange,
      };
    },
    fields: {
      required: ['characters[].name', 'characters[].gender'],
      minArrayLength: 2,
      overrides: {
        TMC: [
          {
            locale: '*',
            required: ['characters[].name', 'characters[].gender', 'characters[].phototype'],
          },
        ]
      }
    }
  },
  multi_birthday: {
    get component() {
      return MultiBirthday;
    },
    get trackingProps() {
      return {
        trackSelectedDay: trackMultiBirthdaySelectedDay,
        trackSelectedMonth: trackMultiBirthdaySelectedMonth,
        trackSelectedYear: trackMultiBirthdaySelectedYear,
      };
    },
    isOptional: true,
    fields: {
      required: ['characters[].day', 'characters[].month', 'characters[].year'],
    },
  },
};

export const getComponent = key => config[key] && config[key].component;
export const getTrackingProps = key => config[key] && config[key].trackingProps || {};
export const getFields = key =>
  (config[key] && config[key].fields) || { required: [] };
export const isFieldOptional = key => config[key] && config[key].isOptional;
export const buildErrors = (errors, raw) => {
  if (isEmpty(errors)) {
    return false;
  }
  return reduce(
    errors,
    (result, value, key) => {
      const message = raw ? value : value[0].message;

      switch (key) {
        case 'gender':
        case 'phototype':
          result.adventurer = {
            ...result.adventurer,
            [key]: message,
          };
          break;
        case 'bears':
          result.bears = value;
          break;
        default:
          result[camelCase(key)] = message;
      }

      return result;
    },
    {},
  );
};
export const clearErrors = (props, name) => {
  if (!name) {
    props.setErrors({});
  }
  if (props.errors && (has(props.errors, name) || props.errors.base)) {
    props.setErrors(omit(props.errors, [name, 'base']));
  }
};

/**
 * updateFieldValues take the current form values and clean up/add defaults if needed
 * @param {Object} fields
 * @returns {Object}
 */
export const updateFieldValues = (fields) => {
  return reduce(
    fields,
    (result, value, key) => {
      result[key] = value;

      // If the current form value is a phototype and it doesn't have a value the just default it to type-iii
      if (key === 'phototype' && !value) {
        result.phototype = 'type-iii';
      }

      // Remove any white space from fields that match and that has a value
      if ((fieldsWithPossibleWhiteSpace.includes(key)) && value) {
        result[key] = value.trim();
      }
      return result;
    },
    {},
  );
};

export const validateCreationForm = (fields, props, config) => {
  config = {
    enableSetErrors: false,
    raw: false,
    ...config,
  };
  props.setSubmitting(true);
  if (!props.productId) {
    props.setSubmitting(false);
    const errors = {
      base: 'ERROR: Missing Product ID',
    };
    if (config.enableSetErrors) {
      props.setErrors(errors);
      return;
    }
    return errors;
  }

  if (!props.country) {
    props.setSubmitting(false);
    const errors = {
      base: 'ERROR: Missing country parameter',
    };
    if (config.enableSetErrors) {
      props.setErrors(errors);
      return;
    }
    return errors;
  }

  const values = updateFieldValues({
    ...props.productCustomisation,
    ...fields,
  });

  const customisations = reduce(values, (result, value, key) => {
    switch (true) {
      case isString(value):
        result[key] = value;
        break;
      case isObjectLike(value):
        result[key] = JSON.stringify(value);
        break;
      case (props.productId === 'lmn' && key === 'phototype' && !value):
        result[key] = 'type-iii';
        break;
      case isNil(value):
        result[key] = '';
        break;
    }

    return result;
  }, {});

  const productData = {
    item: {
      productId: props.productId,
      customisations,
    },
  };

  return validate(productData, props.country.locale).then(res => {
    props.setSubmitting(false);
    const keys = Object.keys(fields);
    const validationErrors = mapKeys(res.body.data.errors, (value, key) =>
      camelCase(key),
    );
    const errors = buildErrors(pick(validationErrors, keys), config.raw);
    if (errors) {
      if (config.enableSetErrors) {
        props.setErrors(errors);
        return true;
      }
      throw errors;
    } else if (!isEmpty(props.errors)) {
      clearErrors(props);
      if (config.enableSetErrors) {
        return true;
      }
    }
  });
};

export const onFieldChange = props => event => {
  let name = event.target.name;

  if (name === 'locale') {
    props.fetchPrismicProduct(props.productId, event.target.value);
  }

  if (name === 'gender' || name === 'phototype') {
    name = `adventurer[${name}]`;
  }

  clearErrors(props, name);

  props.handleChange(event);
};

/**
 * When used with the HOC withHandlers it might register to multiple events which can
 * lose scope, cause race conditions, and other bad things.
 * @returns {function(props): function(event): void}
 */
export const createOnFieldChangeHandler = () => {
  return props => event => {
    return onFieldChange(props)(event);
  };
};






// WEBPACK FOOTER //
// ./src/client/js/view/components/CreationForm/helpers.js